/**
 * 매핑 경로 → 사람이 읽는 이름.
 *
 * 경로 문법은 BE issue 의 `path` 와 같다. 예) `hands.0.observation.position` · `hands.1.action.gripper.input_open` ·
 * `cameras.0.source`. 중복 열 목록(`domain/mapping.ts` 의 `repeatedColumnPaths`)도 같은 문법을 쓴다.
 *
 * 경로의 이름은 폼의 칸 이름과 같다. 사람이 issue 를 읽고 같은 이름의 칸을 찾게 하기 위해서다. 그래서 열 ·
 * 축의 이름도 이 파일이 소유하고 폼(`RoleForm`)이 가져다 쓴다.
 *
 * Owns:
 * - 손 · 역할 · 필드 · 열 · 축의 화면 이름, 경로의 이름.
 *
 * Does not own:
 * - 경로를 폼 칸에 붙이는 일 — 화면.
 */
import type { HandRole, HandRoleName, Mapping, Orientation, Side } from "../domain/mapping";
import type { RoleField } from "./issuePath";

export const HAND_SIDE_LABEL: Readonly<Record<Side, string>> = {
  single: "한 손",
  left: "왼손",
  right: "오른손",
};

export const HAND_ROLE_LABEL: Readonly<Record<HandRoleName, string>> = {
  observation: "관측",
  action: "action",
};

export const ROLE_FIELD_LABEL: Readonly<Record<RoleField, string>> = {
  position: "위치",
  orientation: "자세",
  gripper: "그리퍼",
  reference_frame: "기준 프레임",
  target_frame: "대상 프레임",
  reference_transform: "기준 변환",
  pose_kind: "포즈 종류",
  tcp: "TCP",
  rotation: "손 축",
  reinterpret_reason: "재해석 사유",
};

const SUB_FIELD_LABEL: Readonly<Record<string, string>> = {
  source: "채널 · 열",
  unit: "단위",
  kind: "표현",
  quaternion_order: "quaternion 순서",
  euler_order: "Euler 축 순서",
  euler_frame: "Euler 축 기준",
  angle_unit: "각 단위",
  quantity: "물리량",
  input_closed: "입력 닫힘",
  input_open: "입력 열림",
  clip_range: "clip",
  normalize_quaternion: "비단위 quaternion 정규화",
  x_axis: "X 축",
  y_axis: "Y 축",
  degrees: "추가 각도",
};

const CLIP_END_LABEL = ["clip 아래", "clip 위"] as const;

// 기준 변환의 칸. `kind` 는 자세의 `표현` 과 뜻이 달라 따로 둔다.
const REFERENCE_LEAF_LABEL: Readonly<Record<string, string>> = {
  kind: "종류",
  source_frame: "원본 프레임",
  quaternion_wxyz: "quaternion",
  x_m: "이동 x",
  y_m: "이동 y",
  z_m: "이동 z",
  evidence: "근거",
};

/** 기준 변환 quaternion 의 성분 이름. 폼 칸과 경로 이름이 같이 쓴다. */
export const REFERENCE_QUATERNION_LABELS = ["w", "x", "y", "z"] as const;

const CAMERA_FIELD_LABEL: Readonly<Record<string, string>> = {
  source: "원본 채널",
  slot: "slot",
};

/** 손 축 보정의 X · Y · Z. 추가 각도 칸과 경로 이름이 같이 쓴다. */
export const HAND_AXIS_NAMES = ["X", "Y", "Z"] as const;

export const POSITION_COLUMN_LABELS = ["x", "y", "z"] as const;
export const GRIPPER_COLUMN_LABELS = ["값"] as const;
const MATRIX_COLUMN_LABELS = ["r00", "r01", "r02", "r10", "r11", "r12", "r20", "r21", "r22"];

/** 자세 열의 이름. 종류와 성분 순서가 정한다. 순서를 고르기 전에는 번호다. */
export function orientationColumnLabels(orientation: Orientation): readonly string[] {
  switch (orientation.kind) {
    case "quaternion":
      return orientation.quaternion_order === null
        ? ["1", "2", "3", "4"]
        : orientation.quaternion_order.split("");
    case "euler":
      return orientation.euler_order === null
        ? ["1", "2", "3"]
        : orientation.euler_order.split("").map((axis) => `${axis} 각`);
    case "matrix":
      return MATRIX_COLUMN_LABELS;
    case null:
      return [];
  }
}

function columnLabels(role: HandRole | null, field: string): readonly string[] {
  switch (field) {
    case "position":
      return POSITION_COLUMN_LABELS;
    case "gripper":
      return GRIPPER_COLUMN_LABELS;
    case "orientation":
      return role === null ? [] : orientationColumnLabels(role.orientation);
    default:
      return [];
  }
}

/** 경로 조각이 번호면 그 수, 아니면 `null`. */
function indexOf(segment: string | undefined): number | null {
  return segment !== undefined && /^\d+$/.test(segment) ? Number(segment) : null;
}

/**
 * 필드 아래 경로의 이름. 예) `source.columns.1` → `y 열`, `clip_range.0` → `clip 아래`.
 * 열 이름은 지금 편집본의 구성요소에서 고른다. 구성요소가 없거나 번호가 범위 밖이면 순번으로 쓴다.
 */
function leafLabel(role: HandRole | null, field: string, leaf: readonly string[]): string {
  const [head, next, last] = leaf;
  const index = indexOf(next);
  if (field === "reference_transform") {
    if (
      head === "quaternion_wxyz" &&
      index !== null &&
      index < REFERENCE_QUATERNION_LABELS.length
    ) {
      return `quaternion ${REFERENCE_QUATERNION_LABELS[index]}`;
    }
    return [REFERENCE_LEAF_LABEL[head] ?? head, ...leaf.slice(1)].join(" ");
  }
  if (head === "source" && next === "channel") {
    return "채널";
  }
  if (head === "source" && next === "columns") {
    const column = indexOf(last);
    if (column === null) {
      return "열";
    }
    return `${columnLabels(role, field)[column] ?? `${column + 1}번째`} 열`;
  }
  if (head === "clip_range" && index !== null && index < CLIP_END_LABEL.length) {
    return CLIP_END_LABEL[index];
  }
  if (head === "degrees" && index !== null && index < HAND_AXIS_NAMES.length) {
    return `${HAND_AXIS_NAMES[index]} 추가 각도`;
  }
  return [SUB_FIELD_LABEL[head] ?? head, ...leaf.slice(1)].join(" ");
}

function isRoleName(value: string | undefined): value is HandRoleName {
  return value === "observation" || value === "action";
}

/**
 * 카메라 줄 하나의 이름. 폼의 줄 이름과 같다. 원본이 있으면 원본이고, 없으면 원본 없는 줄 안의 순번이다.
 *
 * @example `[{source: "cam"}, {source: null}]` 의 1번 → `원본 미선택 1`.
 */
export function cameraName(mapping: Mapping, index: number): string {
  const camera = mapping.cameras[index];
  if (camera === undefined) {
    return String(index);
  }
  if (camera.source !== null) {
    return camera.source;
  }
  const unset = mapping.cameras.slice(0, index + 1).filter((item) => item.source === null);
  return `원본 미선택 ${unset.length}`;
}

/** 경로의 이름. 예) `hands.0.observation.position` → `왼손 관측 위치`. 모르는 경로는 그대로 돌려준다. */
export function describeMappingPath(mapping: Mapping, path: string): string {
  const [head, index, ...rest] = path.split(".");
  if (head === "source") {
    return "원본 데이터";
  }
  if (head === "cameras") {
    if (index === undefined) {
      return "카메라";
    }
    // `camera.missing` 은 번호가 아니라 slot 이름을 준다.
    const numbered = /^\d+$/.test(index) && mapping.cameras[Number(index)] !== undefined;
    const name = `카메라 ${numbered ? cameraName(mapping, Number(index)) : index}`;
    const [field] = rest;
    return field === undefined ? name : `${name} ${CAMERA_FIELD_LABEL[field] ?? field}`;
  }
  if (head !== "hands") {
    return path;
  }
  if (index === undefined) {
    return "손 구성";
  }
  const hand = mapping.hands[Number(index)];
  const side = hand === undefined ? `손 ${index}` : HAND_SIDE_LABEL[hand.side];
  const [role, field, ...leaf] = rest;
  if (!isRoleName(role)) {
    // 역할 밖의 손 칸(예: `tcp_offset.x_mm`)은 화면 이름이 없다. 경로 원문을 붙인다.
    return [side, ...rest].join(" ");
  }
  const parts = [side, HAND_ROLE_LABEL[role]];
  if (field === undefined) {
    return parts.join(" ");
  }
  if (!(field in ROLE_FIELD_LABEL)) {
    // 화면 이름이 없는 칸(예: `origin.point` · `motion.kind`)은 경로 원문을 붙인다. 다른 칸의 이름을 빌리지 않는다.
    return [...parts, field, ...leaf].join(" ");
  }
  parts.push(ROLE_FIELD_LABEL[field as RoleField]);
  if (leaf.length > 0) {
    parts.push(leafLabel(hand?.[role] ?? null, field, leaf));
  }
  return parts.join(" ");
}
